<template>
	<HomeKR v-if="headerStore.getCountry === 'KR'" />
	<HomeUSA v-if="headerStore.getCountry === 'USA'" />
	<HomeJPN v-if="headerStore.getCountry === 'JPN'" />
	<HomePopup :exposure-location="'main'" />
	<LiveBroadcast
		v-if="isOpenBroadcast && !!liveBroadcastOnAirData?.livebroadcast"
		:video-src="videoUrl"
		@click-close="isOpenBroadcast = false"
	/>
	<button
		v-if="!!liveBroadcastOnAirData?.livebroadcast"
		class="btn_live"
		aria-label="btn_live"
		@click.stop="onClickLive"
	>
		<div class="btn_live_layout">
			<img src="@/assets/img/common/ellipse.svg" alt="live" loading="lazy" />
			<p>LIVE</p>
		</div>
	</button>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';

import { useGetLiveBroadcastDetailApi, useGetLiveBroadcastOnAirApi } from '@/api/home';
// component
import LiveBroadcast from '@/components/layout/header/LiveBroadcast.vue';
import HomePopup from '@/components/popup/HomePopup.vue';
import { CANCEL, CANT_AIR, COMPANY_TITLE, CONFIRM_LOGIN, LOGIN_REQUIRED } from '@/constants/title';
import HomeJPN from '@/pages/Home/category/HomeJPN.vue';
import HomeKR from '@/pages/Home/category/HomeKR.vue';
import HomeUSA from '@/pages/Home/category/HomeUSA.vue';
// store
import { useHeaderStore } from '@/stores/header';
import { useLoginPopupStore } from '@/stores/LoginPopup';
import { useModalStore } from '@/stores/modal';
import { usePopupStore } from '@/stores/popup';
import { useTokenStore } from '@/stores/token';

const { data: liveBroadcastOnAirData } = useGetLiveBroadcastOnAirApi();
const { data: liveBroadcastDetailData, refetch: refetchLiveBroadcastDetail } = useGetLiveBroadcastDetailApi();

const router = useRouter();
const tokenStore = useTokenStore();
const loginPopupStore = useLoginPopupStore();
const headerStore = useHeaderStore();
const popupStore = usePopupStore();
const modalStore = useModalStore();

const isOpenBroadcast = ref();
const videoUrl = ref('');

// 라이브 버튼 클릭 시
const onClickLive = async () => {
	if (!tokenStore.isLogin) {
		loginPopupStore.openPopup({
			icon: 'alert',
			content: LOGIN_REQUIRED,
			confirm: () => router.push('/auth/login'),
			confirmText: CONFIRM_LOGIN,
			cancelText: CANCEL,
		});
		return;
	}

	await refetchLiveBroadcastDetail();

	if (liveBroadcastDetailData.value) {
		if (!liveBroadcastDetailData.value.success && liveBroadcastDetailData.value.error.code === '58') {
			if (liveBroadcastDetailData.value.data.livebroadcast.livebroadcast_target === 'all') {
				modalStore.openModal('GuideModal');
			}
			if (!isNaN(Number(liveBroadcastDetailData.value.data.livebroadcast.livebroadcast_target)) === true) {
				popupStore.openPopup({
					content: COMPANY_TITLE,
					subtext: CANT_AIR,
				});
			}
			return;
		}

		let url =
			liveBroadcastDetailData.value.data.livebroadcast.livebroadcast_open_yn === 'Y'
				? liveBroadcastDetailData.value.data.livebroadcast.livebroadcast_url
				: '';

		// YouTube URL 파라미터 수정
		if (url) {
			// 기존 URL에서 YouTube ID 추출
			let videoId = '';

			// youtube.com/watch?v= 형식 URL 처리
			if (url.includes('youtube.com/watch?v=')) {
				const match = url.match(/[?&]v=([^&]+)/);
				if (match) videoId = match[1];
			}
			// youtu.be/ 형식 URL 처리
			else if (url.includes('youtu.be/')) {
				const match = url.match(/youtu\.be\/([^?&]+)/);
				if (match) videoId = match[1];
			}

			// ID를 얻었으면 새 URL 생성 (embed 형식으로)
			if (videoId) {
				// 모바일 환경 감지
				const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

				// 모바일에서는 자동 재생 비활성화, PC에서는 활성화
				const autoplayParam = isMobile ? 'autoplay=0' : 'autoplay=1';
				const muteParam = isMobile ? 'mute=0' : 'mute=0';

				// 모바일 호환성을 위한 파라미터 설정 (playsinline 필수)
				url = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&${autoplayParam}&${muteParam}&playsinline=1&controls=0&rel=0&iv_load_policy=3&modestbranding=1&origin=${encodeURIComponent(window.location.origin)}`;
			}
			// ID 추출 실패 시 기존 URL에 파라미터 추가
			else {
				const hasParams = url.includes('?');
				const separator = hasParams ? '&' : '?';

				// 모바일 환경 감지
				const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

				// 모바일에서는 자동 재생 비활성화, PC에서는 활성화
				const autoplayParam = isMobile ? 'autoplay=0' : 'autoplay=1';
				const muteParam = isMobile ? 'mute=0' : 'mute=0';

				url += `${separator}enablejsapi=1&${autoplayParam}&${muteParam}&playsinline=1&controls=0&rel=0`;
			}
		}
		videoUrl.value = url;
		isOpenBroadcast.value = true;
	}
};
</script>

<style lang="scss" scoped>
.btn_live {
	position: fixed;
	bottom: 3rem;
	right: 3rem;
	z-index: 80;
	display: flex;
	width: 5.8rem;
	height: 5.8rem;
	justify-content: center;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
	border-radius: 1000px;
	background: #ff0606;
	box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
	@media screen and (max-width: 1280px) {
		bottom: calc(8.6rem + 7.6rem);
		right: 0rem;
		width: 5.2rem;
		height: 4.2rem;
		border-radius: 1000px 0px 0px 1000px;
		box-shadow: 4px 4px 30px 0px rgba(0, 0, 0, 0.3);
		padding: 0.7rem 0.3rem 0.4rem 0.8rem;
	}
	.btn_live_layout {
		display: flex;
		flex-direction: row;
		img {
			width: 4px;
			height: 4px;
		}
		p {
			color: #fff;
			font-size: var(--font-s-regular);
			font-weight: var(--font-w-bold);
			line-height: 2.3rem;
			@media screen and (max-width: 1280px) {
				font-size: var(--font-s-mid);
				line-height: 1.9rem;
			}
		}
	}
}
</style>
